import React from 'react';
import {
    getWindowViewport,
    loadPanelSessionState,
    normalizePanelSessionState,
    savePanelSessionState,
} from './panelSessionState';
import type { PanelSessionState, PanelSessionStateUpdate } from './panelSessionState';

export const useSessionPanelState = (panelId: string, defaults: PanelSessionState) => {
    const defaultsRef = React.useRef(defaults);

    React.useEffect(() => {
        defaultsRef.current = defaults;
    }, [defaults]);

    const [panelState, setPanelState] = React.useState<PanelSessionState>(() => {
        return loadPanelSessionState(panelId, defaults);
    });

    const updatePanelState = React.useCallback((update: PanelSessionStateUpdate) => {
        setPanelState(currentState => {
            const updateValue = typeof update === 'function' ? update(currentState) : update;
            const nextValue = { ...currentState, ...updateValue };
            const normalizedState = normalizePanelSessionState(nextValue, defaultsRef.current, getWindowViewport());
            savePanelSessionState(panelId, normalizedState);
            return normalizedState;
        });
    }, [panelId]);

    React.useEffect(() => {
        const handleResize = () => {
            setPanelState(currentState => {
                const normalizedState = normalizePanelSessionState(currentState, defaultsRef.current, getWindowViewport());
                savePanelSessionState(panelId, normalizedState);
                return normalizedState;
            });
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [panelId]);

    return [panelState, updatePanelState] as const;
};